import { useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { FormConfig, FormConfigItem, FormValues } from "../../types/form";
import type { UseFormPagesResult } from "../../hooks/useFormPages";
import { FormItems } from "../FormItems/FormItems";
import { FormPageNavigation } from "../FormPageNavigation/FormPageNavigation";
import { ReviewPage } from "../ReviewPage/ReviewPage";
import { isReviewPage } from "../ReviewPage/reviewPage.helpers";

const DEFAULT_SUBMIT_LABEL = "Submit";
const REVIEW_LABEL = "Review";

const VALIDATABLE_ITEM_TYPES = new Set(["field", "consent", "recaptcha"]);

function getFieldSlugsFromItems(items: FormConfigItem[]): Set<string> {
  const slugs = new Set<string>();
  for (const item of items) {
    if (VALIDATABLE_ITEM_TYPES.has(item.type)) slugs.add(item.slug);
    if (item.type === "section" && item.params.items) {
      for (const child of item.params.items) {
        if (VALIDATABLE_ITEM_TYPES.has(child.type)) slugs.add(child.slug);
      }
    }
  }
  return slugs;
}

export interface MultiPageFormPage {
  id: string;
  name: string;
  items: FormConfigItem[];
}

interface MultiPageFormProps {
  config: FormConfig;
  pages: MultiPageFormPage[];
  formPages: UseFormPagesResult;
  isSubmitting: boolean;
  onSubmit: () => void;
  values: FormValues;
}

export const MultiPageForm: React.FC<MultiPageFormProps> = ({
  config,
  pages,
  formPages,
  isSubmitting,
  onSubmit,
  values,
}) => {
  const formik = useFormikContext<FormValues>();
  const {
    currentPage,
    currentPageIndex,
    isFirstPage,
    isLastPage,
    isSinglePage,
    goNext,
    goPrevious,
    goToPage,
  } = formPages;
  const currentPageIsReview = isReviewPage(currentPage);
  const nextPage = pages[currentPageIndex + 1];
  const nextStepLabel = nextPage && isReviewPage(nextPage) ? REVIEW_LABEL : undefined;
  const currentPageItems = currentPage.items;
  const nonReviewPages = useMemo(() => pages.filter((page) => !isReviewPage(page)), [pages]);
  const isInitialRenderRef = useRef(true);

  // Aggregate items from every original page so condition chains crossing page
  // boundaries resolve correctly (e.g., a field on page 2 depending on a field on
  // page 1 that is hidden by its own condition but retains a stale Formik value).
  const allPagesItems = useMemo(() => pages.flatMap((page) => page.items), [pages]);

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }

    // Scrollbar (react-scrollbars-custom) renders the scroller as the parent of the content element
    const scrollableElement =
      document.querySelector(".page-layout__content")?.parentElement ??
      document.scrollingElement ??
      document.documentElement;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    scrollableElement.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }, [currentPageIndex]);

  const handleNext = useCallback(async () => {
    if (currentPageIsReview) return;

    const currentPageSlugs = getFieldSlugsFromItems(currentPageItems);
    const slugs = Array.from(currentPageSlugs);

    // Touch each field and its "Other" option description — that description lives in a
    // separate metadata_<slug> Formik field — so validators run and errors show.
    await Promise.all(
      slugs.flatMap((slug) => [
        formik.setFieldTouched(slug, true, true),
        formik.setFieldTouched(`metadata_${slug}.description`, true, true),
      ])
    );

    // Re-validate the whole form after touching. A field blocks navigation if it errors
    // directly OR its "Other" option description (metadata_<slug>) is invalid.
    const errors = await formik.validateForm();
    const erroredKeys = new Set(Object.keys(errors));
    const firstErrorSlug = slugs.find(
      (slug) => erroredKeys.has(slug) || erroredKeys.has(`metadata_${slug}`)
    );

    if (firstErrorSlug) {
      const element = document.querySelector(`[data-field-name='${firstErrorSlug}']`);
      if (element) {
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        element.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "center",
        });
      }
      return;
    }

    goNext();
    // formik.validateForm and formik.setFieldTouched are stable refs;
    // omit formik object itself to avoid recreating on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIsReview, currentPageItems, goNext]);

  return (
    <>
      {currentPageIsReview ? (
        <ReviewPage
          config={config}
          formPages={nonReviewPages}
          isSubmitting={isSubmitting}
          onEditPage={goToPage}
          onPrevious={goPrevious}
          onSubmit={onSubmit}
          reviewPage={currentPage}
          submitLabel={config.form_state?.submit_label ?? DEFAULT_SUBMIT_LABEL}
          values={values}
        />
      ) : (
        <>
          <FormItems
            config={config}
            formItems={currentPageItems}
            formValues={values}
            visibilityItems={allPagesItems}
          />

          <FormPageNavigation
            isFirstPage={isFirstPage}
            isLastPage={isLastPage}
            isSinglePage={isSinglePage}
            isSubmitting={isSubmitting}
            nextLabel={nextStepLabel}
            submitLabel={config.form_state?.submit_label ?? DEFAULT_SUBMIT_LABEL}
            onNext={() => {
              handleNext().catch(console.error);
            }}
            onPrevious={goPrevious}
            onSubmit={onSubmit}
          />
        </>
      )}
    </>
  );
};
